import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, CircularProgress, Drawer } from '@mui/material';
import { Outlet } from 'react-router-dom';
import type { Me } from '@serb/shared';
import { KEYS } from '../api/keys';
import { signOut } from '../features/auth/passkeys';
import { useLoadSettings } from '../features/settings/useLoadSettings';
import { useSummary } from '../features/home/useSummary';
import { useLive, type LiveStatus } from '../lib/useLive';
import { SIDEBAR } from '../theme';
import { BOTTOM_NAV_HEIGHT, BottomNav } from './BottomNav';
import { SideMenu } from './SideMenu';
import { TopBar } from './TopBar';

/** What the shell hands to every page */
export type ShellContext = { live: LiveStatus };

/** What every signed-in page sits inside: the side menu, the top bar, then the page itself */
export function AppShell({ me }: { me: Me }) {
  const qc = useQueryClient();
  const live = useLive();
  const summary = useSummary();
  const [open, setOpen] = useState(false);
  const loaded = useLoadSettings();

  async function handleSignOut() {
    await signOut();
    qc.setQueryData(KEYS.me, null);
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <Box component="aside" sx={{ width: { md: SIDEBAR.width }, flexShrink: 0 }}>
        <Drawer
          variant="permanent"
          open
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: SIDEBAR.width, border: 0 } }}
        >
          <SideMenu approvals={summary.approvals} />
        </Drawer>
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          sx={{ display: { md: 'none' }, '& .MuiDrawer-paper': { width: SIDEBAR.width, maxWidth: '85vw', border: 0 } }}
        >
          <SideMenu approvals={summary.approvals} onPick={() => setOpen(false)} />
        </Drawer>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TopBar device={me.device} live={live} onMenu={() => setOpen(true)} onSignOut={() => void handleSignOut()} />
        <Box
          component="main"
          sx={{
            p: { xs: 1.5, md: 3 },
            pl: { xs: 'max(12px, env(safe-area-inset-left))', md: 3 },
            pr: { xs: 'max(12px, env(safe-area-inset-right))', md: 3 },
            pb: { xs: `calc(${BOTTOM_NAV_HEIGHT + 12}px + env(safe-area-inset-bottom))`, md: 3 },
            maxWidth: 1700,
            mx: 'auto',
          }}
        >
          {loaded ? (
            <Outlet context={{ live } satisfies ShellContext} />
          ) : (
            <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 8 }} />
          )}
        </Box>
      </Box>
      <BottomNav approvals={summary.approvals} onMore={() => setOpen(true)} />
    </Box>
  );
}
